import 'simplebar-react/dist/simplebar.min.css';

import ArrowDropDownTwoToneIcon from '@mui/icons-material/ArrowDropDownTwoTone';
import ArrowDropUpTwoToneIcon from '@mui/icons-material/ArrowDropUpTwoTone';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import {camelCase, get, orderBy, startCase, upperFirst} from 'lodash';
import {
  createRef,
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Draggable from 'react-draggable';
import type SimpleBarCore from 'simplebar-core';
import SimpleBar from 'simplebar-react';

import {usePrint} from '../../../hooks/print';
import {VerticalRule} from '../../../images/icon/VerticalRule';
import TruncatedText from '../TruncantedText';
import {DataGridColumnActionsButton} from './DataGridActionsButton';
import {DataGridArrangeColumnsModal} from './DataGridArrangeColumnsModal';
import {
  clearTransform,
  DraggableLayout,
  handleDrag,
  handleDragStop,
} from './Draggable/utils';

interface Props<T extends DataGridRow> {
  rows: T[];
  columns: DataGridColumn<T>[];
  sortBy?: DataGridSortBy | null;
  sortingMode?: 'client' | 'server';
  pagination?: boolean;
  paginationMode?: 'client' | 'server';
  page?: number;
  pageSize?: number;
  rowCount?: number;
  shownFields?: string[];
  defaultShownFields?: string[];
  columnWidths?: Record<string, number>;
  columnFilters?: Record<string, string>;
  loading?: boolean;
  customNoDataMessage?: string;
  footerStart?: ReactNode;
  size?: 'small' | 'medium';
  showHeader?: boolean;
  sx?: SxProps<Theme>;
  sxHeader?: SxProps<Theme>;
  getSxTr?: (row: T) => SxProps<Theme> | undefined;
  sxTh?: SxProps<Theme>;
  sxFooter?: SxProps<Theme>;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  onSort?: (sortBy?: DataGridSortBy) => void;
  onShownFieldsChange?: (shownFields: string[]) => void;
  onRowClick?: (row: T) => void;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  onColumnFiltersChange?: (filters: Record<string, string>) => void;
  draggable?: boolean;
  draggableField?: string;
  draggableClass?: string;
  draggableCallback?: (result: any) => void;
  resizeable?: boolean;
  columnOrder?: string[];
  onColumnOrderChange?: (order: string[]) => void;
  onColumnOrderAndShownFieldsChange?: (order: string[], fields: string[]) => void;
}

export interface DataGridRef {
  printTable: () => void;
  tableRef: MutableRefObject<HTMLTableElement | null>;
}

const isValueType = (value?: DataGridValue) =>
  value instanceof Date ||
  value === null ||
  value === undefined ||
  ['number', 'string', 'boolean'].includes(typeof value);

const titleCase = (value: string) =>
  upperFirst(startCase(camelCase(value)).toLowerCase());

export interface DataGridRow {
  [x: string]: any;
}

export type DataGridValue = string | number | Date | null | undefined | boolean;
export type ColumnType = 'link' | 'icon';
export type DataGridSortDir = 'desc' | 'asc';

export interface DataGridSortBy {
  field: string;
  dir: DataGridSortDir;
}

export interface DataGridColumn<T extends DataGridRow> {
  field: string;
  headerName?: string;
  sortable?: boolean;
  sortModel?: {
    field: string;
    sort?: 'asc' | 'desc';
  }[];
  type?: 'select' | 'actions';
  width?: number;
  hideable?: boolean;
  hidden?: boolean;
  filterable?: boolean;
  filterPlaceholder?: string;
  sxCell?: SxProps<Theme>;
  getSxCell?: (params: RenderCellParams<T>) => SxProps<Theme>;
  sxHeader?: SxProps<Theme>;
  renderHeader?: (params: RenderHeaderParams) => ReactNode;
  renderCell?: (params: RenderCellParams<T>) => ReactNode;
  valueGetter?: (params: ValueGetterParams<T>) => DataGridValue | undefined;
  valueFormatter?: (params: ValueFormatterParams<T>) => string;
  doNotExport?: boolean;
  resizeable?: boolean;
  alignCenter?: boolean;
  flexible?: boolean;
  minWidth?: number;
}

export type DataGridViewProps<T extends DataGridRow> = {
  refObj: React.RefObject<DataGridRef>;
  rows: T[];
  columns: DataGridColumn<T>[];
  page: number;
  pageSize: number;
  loading?: boolean;
  shownFields?: string[];
  defaultShownFields?: string[];
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  columnFilters?: Record<string, string>;
  sxFooter: object;
  footerStart: React.ReactNode;
  onShownFieldsChange: (v: string[]) => void;
  onColumnWidthsChange: (v: Record<string, number>) => void;
  onColumnOrderChange?: (v: string[]) => void;
  onColumnOrderAndShownFieldsChange?: (order: string[], fields: string[]) => void;
  onColumnFiltersChange?: (v: Record<string, string>) => void;
  onPageChange: (v: number) => void;
  onPageSizeChange: (v: number) => void;
};

export interface RenderHeaderParams {
  field: string;
  headerName?: string;
}

export interface RenderCellParams<T extends DataGridRow> {
  row: T;
  field: string;
  value?: DataGridValue;
  valueFormatter?: (params: ValueFormatterParams<T>) => string;
}

export interface ValueGetterParams<T extends DataGridRow> {
  row: T;
  field: string;
}

export interface ValueFormatterParams<T extends DataGridRow> {
  row: T;
  field: string;
  value?: DataGridValue;
}

export const defaultValueFormatter = <T extends DataGridRow>({
  value,
}: ValueFormatterParams<T>) => {
  if (value instanceof Date) {
    return dayjs(value).format('YYYY-DD-MM HH:mm');
  }

  return value;
};

export const defaultRenderCell = <T extends DataGridRow>({
  row,
  field,
  valueFormatter,
  value,
}: RenderCellParams<T>) => {
  const formattedValue = (valueFormatter ?? defaultValueFormatter)({
    field,
    row,
    value,
  });
  return (
    <TruncatedText
      title={formattedValue?.toString()}
      sx={{
        '@media print': {
          whiteSpace: 'normal',
        },
      }}
    >
      {formattedValue}
    </TruncatedText>
  );
};

export const defaultRenderHeader = ({
  field,
  headerName,
}: RenderHeaderParams) => {
  const title = headerName ?? titleCase(field);
  return (
    <TruncatedText
      title={title}
      fontWeight={600}
      sx={{
        '@media print': {
          whiteSpace: 'normal',
        },
      }}
    >
      {title}
    </TruncatedText>
  );
};

const canFieldBeHidable = (field: string) =>
  !['select', 'actions'].includes(field);

const canFieldBePrintable = (field: string) =>
  !['select', 'actions'].includes(field);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 500, 1000, 5000, 10000];

const DataGrid = forwardRef(
  <T extends DataGridRow>(
    {
      columns,
      rows,
      sortBy: externalSortBy,
      sortingMode,
      pagination,
      paginationMode = 'client',
      page: externalPage,
      pageSize: externalPageSize,
      rowCount: externalRowCount,
      shownFields: externalShownFields,
      defaultShownFields,
      columnWidths: externalColumnWidths,
      columnFilters: externalColumnFilters,
      loading,
      customNoDataMessage,
      footerStart,
      size = 'small',
      showHeader = true,
      sx,
      sxHeader,
      getSxTr,
      sxTh,
      sxFooter,
      onPageSizeChange,
      onPageChange,
      onSort,
      onShownFieldsChange,
      onRowClick,
      onColumnWidthsChange,
      onColumnFiltersChange,
      draggable,
      draggableField,
      draggableClass,
      draggableCallback,
      resizeable = true,
      columnOrder: externalColumnOrder,
      onColumnOrderChange,
      onColumnOrderAndShownFieldsChange,
    }: Props<T>,
    ref?: ForwardedRef<DataGridRef>
  ) => {
    columns = columns.filter((i) => !i.hidden);
    /**
     * shown fields
     */

    const columnsCanBeHidable = useMemo(
      () => columns.filter((i) => canFieldBeHidable(i.field)),
      [columns]
    );

    const fieldsCanBeHidable = useMemo(
      () => columnsCanBeHidable.map((i) => i.field),
      [columnsCanBeHidable]
    );

    const [internalShownFields, setInternalShownFields] = useState(
      externalShownFields ?? fieldsCanBeHidable
    );

    const shownFields = useMemo(() => {
      if (externalShownFields !== undefined) {
        return externalShownFields;
      }
      return internalShownFields;
    }, [externalShownFields, internalShownFields]);

    const [internalColumnWidths, setInternalColumnWidths] = useState<
      Record<string, number>
    >(() => {
      const initialWidths: Record<string, number> = {};

      columns.forEach((col) => {
        const key = `column-${col.field}`;

        const widthFromParent = externalColumnWidths?.[key];
        const widthFromColumn = col.width;
        const finalWidth = widthFromParent ?? widthFromColumn ?? 60;

        initialWidths[key] = finalWidth;
      });

      return initialWidths;
    });

    const handleShowFields = (fields: string[]) => {
      setInternalShownFields(fields);
      onShownFieldsChange?.(fields);
    };

    const shownColumns = useMemo(
      () =>
        columns.filter(
          (i) => !canFieldBeHidable(i.field) || shownFields.includes(i.field)
        ),
      [columns, shownFields]
    );

    /**
     * column order
     */

    const [internalColumnOrder, setInternalColumnOrder] = useState<string[]>(
      () => externalColumnOrder ?? columns.map((c) => c.field)
    );

    const columnOrder = useMemo(() => {
      const allFields = shownColumns.map((c) => c.field);
      const savedOrder = externalColumnOrder !== undefined ? externalColumnOrder : internalColumnOrder;

      if (!savedOrder || savedOrder.length === 0) {
        return allFields;
      }

      const savedSet = new Set(savedOrder);
      const newFieldsWithIndexes = allFields
        .map((field, index) => ({field, index}))
        .filter(({field}) => !savedSet.has(field));

      if (newFieldsWithIndexes.length === 0) {
        return savedOrder;
      }

      const result = [...savedOrder];
      newFieldsWithIndexes.forEach(({field, index}) => {
        result.splice(index, 0, field);
      });

      return result;
    }, [externalColumnOrder, internalColumnOrder, shownColumns]);

    const handleColumnOrderChange = (order: string[]) => {
      setInternalColumnOrder(order);
      onColumnOrderChange?.(order);
    };

    const orderedColumns = useMemo(() => {
      if (!columnOrder || columnOrder.length === 0) {
        return shownColumns;
      }

      const columnMap = new Map(shownColumns.map((col) => [col.field, col]));
      const ordered: typeof shownColumns = [];
      const processedFields = new Set<string>();

      columnOrder.forEach((field) => {
        const col = columnMap.get(field);
        if (col) {
          ordered.push(col);
          processedFields.add(field);
        }
      });

      shownColumns.forEach((col) => {
        if (!processedFields.has(col.field)) {
          ordered.push(col);
        }
      });

      return ordered;
    }, [shownColumns, columnOrder]);

    /**
     * column filters
     */

    const [internalColumnFilters, setInternalColumnFilters] = useState<
      Record<string, string>
    >(externalColumnFilters ?? {});

    const columnFilters = useMemo(() => {
      return externalColumnFilters !== undefined
        ? externalColumnFilters
        : internalColumnFilters;
    }, [externalColumnFilters, internalColumnFilters]);

    const handleColumnFiltersChange = (filters: Record<string, string>) => {
      setInternalColumnFilters(filters);
      onColumnFiltersChange?.(filters);
    };

    /**
     * data
     */

    const data = useMemo(
      () =>
        rows.map((row) => ({
          row,
          columnValues: orderedColumns.map(
            ({valueGetter, field}): DataGridValue => {
              const v = valueGetter
                ? valueGetter({row, field})
                : get(row, field);
              return isValueType(v) ? v : '-';
            }
          ),
        })),
      [rows, orderedColumns]
    );

    /**
     * filtering
     */

    const filteredData = useMemo(() => {
      const activeFilters = Object.entries(columnFilters).filter(
        ([_, value]) => value.trim() !== ''
      );

      if (activeFilters.length === 0) {
        return data;
      }

      return data.filter((item) => {
        return activeFilters.every(([field, filterValue]) => {
          const column = columns.find((col) => col.field === field);
          if (!column) {
            return true;
          }

          const cellValue = column.valueGetter
            ? column.valueGetter({row: item.row, field})
            : get(item.row, field);

          const searchValue = filterValue.toLowerCase().trim();

          if (cellValue === null || cellValue === undefined) {
            return false;
          }

          return String(cellValue).toLowerCase().includes(searchValue);
        });
      });
    }, [data, columnFilters, columns]);

    /**
     * sorting
     */

    const [internalSortBy, setInternalSortBy] = useState<
      DataGridSortBy | undefined | null
    >(externalSortBy);

    const sortBy = useMemo(() => {
      if (sortingMode === 'server') {
        return externalSortBy;
      }
      return externalSortBy !== undefined ? externalSortBy : internalSortBy;
    }, [sortingMode, externalSortBy, internalSortBy]);

    const sortByColumnIndex = useMemo(() => {
      if (sortBy) {
        return shownColumns.findIndex((i) => i.field === sortBy.field);
      }
    }, [shownColumns, sortBy]);

    const sortedData = useMemo(() => {
      if (sortingMode === 'server') {
        return filteredData;
      }
      if (sortByColumnIndex !== undefined && sortByColumnIndex >= 0) {
        return orderBy(
          filteredData,
          (i) => i.columnValues[sortByColumnIndex] ?? '',
          sortBy?.dir
        );
      }
      return filteredData;
    }, [filteredData, sortBy, sortByColumnIndex]);

    const handleSort = (field?: string, dir?: 'desc' | 'asc') => {
      const newDir =
        dir ??
        (sortBy?.dir === 'asc' && field === sortBy.field ? 'desc' : 'asc');

      const newSortBy: DataGridSortBy | undefined = field
        ? {
            field,
            dir: newDir,
          }
        : undefined;

      setInternalSortBy(newSortBy);
      onSort?.(newSortBy);
    };

    /**
     * pagination
     */

    const [internalPage, setInternalPage] = useState(externalPage ?? 0);
    const [internalPageSize, setInternalPageSize] = useState(
      externalPageSize ?? 25
    );

    const page = useMemo(() => {
      if (paginationMode === 'server') {
        return externalPage ?? 0;
      }
      return externalPage !== undefined ? externalPage ?? 0 : internalPage;
    }, [paginationMode, externalPage, internalPage]);

    const pageSize = useMemo(() => {
      if (paginationMode === 'server') {
        return externalPageSize ?? 10;
      }
      return externalPageSize !== undefined
        ? externalPageSize ?? 10
        : internalPageSize;
    }, [paginationMode, externalPageSize, internalPageSize]);

    const pageData = useMemo(() => {
      if (paginationMode === 'server') {
        return sortedData;
      }
      return sortedData.slice(pageSize * page, pageSize * page + pageSize);
    }, [paginationMode, page, pageSize, sortedData]);

    const rowCount = useMemo(() => {
      if (paginationMode === 'server') {
        return externalRowCount ?? 0;
      }
      return sortedData.length;
    }, [paginationMode, externalRowCount, sortedData]);

    const handlePageChange = (v: number) => {
      setInternalPage(v);
      onPageChange?.(v);
    };

    const handlePageSizeChange = (v: number) => {
      setInternalPageSize(v);
      onPageSizeChange?.(v);
      setInternalPage(0);
    };

    /**
     * column menu state for filter focus
     */

    const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState<{
      field: string;
      element: HTMLElement;
    } | null>(null);

    const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);

    const [arrangeModalOpen, setArrangeModalOpen] = useState(false);

    const maxPage = Math.floor(rowCount / pageSize);

    useEffect(() => {
      if (maxPage < page) {
        setTimeout(() => {
          handlePageChange(maxPage);
        });
      }
    }, [maxPage, page]);

    /**
     * print
     */

    const tableRef = useRef(null);
    const printContainerRef = useRef<HTMLDivElement>(null);
    const [printTable] = usePrint(printContainerRef.current);

    /**
     * ref
     */

    useImperativeHandle(ref, () => ({
      printTable,
      tableRef,
    }));

    const tableBodyRef = useRef(null);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const headContainerRef = useRef<HTMLDivElement | null>(null);
    const simpleBarRef = useRef<SimpleBarCore | null>(null);

    useEffect(() => {
      const scroller = scrollContainerRef.current;
      const head = headContainerRef.current;

      if (!scroller || !head) {
        return;
      }

      const handleScroll = () => {
        head.scrollLeft = scroller.scrollLeft;
      };

      scroller.addEventListener('scroll', handleScroll);
      return () => scroller.removeEventListener('scroll', handleScroll);
    }, []);

    const draggableLayout = useMemo<DraggableLayout>(() => {
      const l: DraggableLayout = [];
      let order = 0;

      const p = draggableField
        ? pageData.filter((d) => d.row[draggableField])
        : pageData;

      p.forEach((item, index: number) => {
        l.push({
          position: order++,
          index,
          item,
          ref: createRef<HTMLTableRowElement>(),
        });
      });

      return l;
    }, [pageData]);

    useEffect(() => {
      if (draggable) {
        clearTransform(draggableLayout);
      }
    }, [draggableLayout]);

    const renderTableRow = (
      i: (typeof pageData)[0],
      rowIdx: number,
      additionalSx?: SxProps<Theme>
    ) => (
      <TableRow
        ref={
          draggable
            ? draggableLayout.filter((el) => el.index === rowIdx)[0]?.ref
            : undefined
        }
        key={rowIdx}
        hover
        sx={[getSxTr?.(i.row), additionalSx].filter(Boolean) as SxProps<Theme>}
        onClick={() => onRowClick?.(i.row)}
      >
        {orderedColumns.map((column, columnIdx) => (
          <TableCell
            key={`header-row-${rowIdx}-${column.field.toString()}`}
            padding={column.type === 'select' ? 'checkbox' : undefined}
            sx={{
              padding: '6px 15px 6px 5px',
              ...(resizeable &&
                !column.flexible &&
                column.type !== 'select' && {
                  width:
                    internalColumnWidths[`column-${column.field.toString()}`] ??
                    60,
                  minWidth:
                    internalColumnWidths[`column-${column.field.toString()}`] ??
                    60,
                  maxWidth:
                    internalColumnWidths[`column-${column.field.toString()}`] ??
                    60,
                }),
              ...(resizeable &&
                column.flexible && {
                  minWidth: column.minWidth ?? 120,
                }),
              ...(!resizeable && {
                width: column.width ?? 30,
              }),
              color: 'inherit',
              '@media print': {
                minWidth: 'auto',
                px: 1.5,
                display: !canFieldBePrintable(column.field)
                  ? 'none'
                  : undefined,
              },
              ...(column.sxCell ||
                column.getSxCell?.({
                  field: column.field,
                  row: i.row,
                  value: i.columnValues[columnIdx],
                  valueFormatter: column.valueFormatter,
                })),
            }}
          >
            {(column.renderCell ?? defaultRenderCell)({
              field: column.field,
              row: i.row,
              value: i.columnValues[columnIdx],
              valueFormatter: column.valueFormatter,
            })}
          </TableCell>
        ))}
        {resizeable && (
          <TableCell
            style={{width: 20, minWidth: 20}}
            sx={{
              pointerEvents: 'none',
              border: 'none',
              background: 'transparent',
            }}
          />
        )}
      </TableRow>
    );

    const handleResizeStart = (
      e: React.MouseEvent,
      column: DataGridColumn<any>
    ) => {
      const startX = e.clientX;
      const key = `column-${column.field}`;
      const startWidth =
        internalColumnWidths[key] ??
        externalColumnWidths?.[key] ??
        column.width ??
        60;

      const updatedWidths = {...internalColumnWidths};

      const handleMouseMove = (event: MouseEvent) => {
        const delta = event.clientX - startX;
        const newWidth = Math.max(50, startWidth + delta);

        updatedWidths[key] = newWidth;
        setInternalColumnWidths({...updatedWidths});
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        simpleBarRef.current?.recalculate();

        onColumnWidthsChange?.(updatedWidths);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    return (
      <Box
        ref={printContainerRef}
        sx={{
          height: '100%',
          overflow: 'hidden',
          bgcolor: (theme) => theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          ...sx,
        }}
      >
        <Box
          ref={headContainerRef}
          sx={{
            height: 40,
            flexShrink: 0,
            overflowX: 'hidden',
            overflowY: 'hidden',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Table
            stickyHeader
            ref={tableRef}
            size={size}
            sx={{
              overflow: 'hidden',
              bgcolor: 'inherit',
              minWidth: '100%',
              ...(resizeable && {tableLayout: 'fixed'}),
            }}
          >
            {showHeader ? (
              <TableHead
                sx={{
                  bgcolor: 'background.paper',
                  ...sxHeader,
                }}
              >
                <TableRow
                  sx={{
                    bgcolor: 'inherit',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  {orderedColumns.map((column) => (
                    <TableCell
                      key={`column-${column.field.toString()}`}
                      style={{
                        ...(resizeable &&
                          !column.flexible &&
                          column.type !== 'select' && {
                            width:
                              internalColumnWidths[
                                `column-${column.field.toString()}`
                              ] ?? 60,
                            minWidth:
                              internalColumnWidths[
                                `column-${column.field.toString()}`
                              ] ?? 60,
                            maxWidth:
                              internalColumnWidths[
                                `column-${column.field.toString()}`
                              ] ?? 60,
                            boxSizing: 'border-box',
                          }),
                        ...(resizeable &&
                          column.flexible && {
                            minWidth: column.minWidth ?? 120,
                          }),
                        ...(!resizeable && {
                          width: column.width,
                        }),
                      }}
                      padding={
                        column.type === 'select' ? 'checkbox' : undefined
                      }
                      sx={
                        {
                          ...{
                            maxWidth: 300,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            fontWeight: 600,
                            bgcolor: 'background.paper',
                            padding: '6px 15px 6px 5px',
                            lineHeight: 1.1,
                            '&:hover .title': {
                              maxWidth: 'calc(100% - 12px)',
                              overflow: 'hidden',
                            },
                            '&:hover .actions': {
                              opacity: 1,
                            },
                            '&:hover .filter': {},
                            '@media print': {
                              minWidth: 'auto',
                              whiteSpace: 'normal',
                              px: 1.5,
                              display: !canFieldBePrintable(column.field)
                                ? 'none'
                                : undefined,
                            },
                          },
                          ...sxTh,
                          ...column.sxHeader,
                        } as SxProps<Theme>
                      }
                    >
                      {column.type && ['select'].includes(column.type) ? (
                        (column.renderHeader ?? defaultRenderHeader)({
                          field: column.field,
                          headerName: column.headerName,
                        })
                      ) : (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={
                            column.alignCenter ? 'center' : 'space-between'
                          }
                        >
                          <Box
                            className="title"
                            display="flex"
                            alignItems="center"
                            onMouseEnter={() => setHoveredColumn(column.field)}
                            onMouseLeave={() => setHoveredColumn(null)}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              py={0.5}
                              sx={
                                column.filterable
                                  ? {cursor: 'pointer'}
                                  : undefined
                              }
                              onClick={(e) => {
                                if (column.filterable) {
                                  setColumnMenuAnchorEl({
                                    field: column.field,
                                    element: e.currentTarget as HTMLElement,
                                  });
                                }
                              }}
                            >
                              {(column.renderHeader ?? defaultRenderHeader)({
                                field: column.field,
                                headerName: column.headerName,
                              })}
                            </Box>

                            <Box
                              className="filter"
                              mr={-0}
                              display="flex"
                              alignItems="center"
                            >
                              {columnFilters[column.field]?.trim() ? (
                                <Tooltip
                                  title={`Filtered: "${columnFilters[column.field]}"`}
                                >
                                  <FilterListIcon
                                    fontSize="small"
                                    color="primary"
                                    sx={{ml: 0.5, cursor: 'pointer'}}
                                    onClick={(e) => {
                                      setColumnMenuAnchorEl({
                                        field: column.field,
                                        element: e.currentTarget as unknown as HTMLElement,
                                      });
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                              {(hoveredColumn === column.field ||
                                sortBy?.field === column.field) &&
                              column.sortable ? (
                                <IconButton
                                  sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                    ...(sortBy?.field === column.field
                                      ? {}
                                      : {
                                          color: 'action.active',
                                          opacity: 0.7,
                                        }),
                                  }}
                                  color={
                                    sortBy?.field === column.field
                                      ? 'primary'
                                      : 'default'
                                  }
                                  onClick={() => handleSort(column.field)}
                                  aria-label={`Sort by ${
                                    column.headerName ?? column.field
                                  } ${
                                    sortBy?.field === column.field
                                      ? sortBy.dir === 'asc'
                                        ? 'descending'
                                        : 'ascending'
                                      : 'ascending'
                                  }`}
                                >
                                  {sortBy?.field === column.field ? (
                                    sortBy.dir === 'desc' ? (
                                      <ArrowDropDownTwoToneIcon fontSize="small" />
                                    ) : (
                                      <ArrowDropUpTwoToneIcon fontSize="small" />
                                    )
                                  ) : (
                                    <ArrowDropUpTwoToneIcon fontSize="small" />
                                  )}
                                </IconButton>
                              ) : null}
                            </Box>
                          </Box>

                          <Box
                            className="actions"
                            position="absolute"
                            right={10}
                            sx={{opacity: 0, bgcolor: 'none'}}
                          >
                            <DataGridColumnActionsButton
                              column={column}
                              sortBy={sortBy}
                              columnFilters={columnFilters}
                              shouldFocusFilter={
                                columnMenuAnchorEl?.field === column.field
                              }
                              externalAnchorEl={
                                columnMenuAnchorEl?.field === column.field
                                  ? columnMenuAnchorEl.element
                                  : null
                              }
                              onSort={(v) => handleSort(column.field, v)}
                              onColumnFiltersChange={handleColumnFiltersChange}
                              onMenuClose={() => setColumnMenuAnchorEl(null)}
                              onArrangeColumnsClick={() =>
                                setArrangeModalOpen(true)
                              }
                            />
                          </Box>
                        </Box>
                      )}
                      {column.resizeable ? (
                        <Box
                          onMouseDown={(e) => handleResizeStart(e, column)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '10px',
                            height: '100%',
                            cursor: 'col-resize',
                            zIndex: 1,
                            verticalAlign: 'middle',
                            display: 'flex',
                            alignItems: 'center',
                            '&:hover .icon': {opacity: 1},
                          }}
                        >
                          <VerticalRule
                            size="large"
                            className="icon"
                            sx={{
                              opacity: '0.5',
                              transition: 'opacity 0.3s',
                              width: 10,
                              left: -4,
                              position: 'absolute',
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: '10px',
                            height: '100%',
                            zIndex: 1,
                            verticalAlign: 'middle',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <VerticalRule
                            size="large"
                            className="icon"
                            sx={{
                              opacity: '0.5',
                              transition: 'opacity 0.3s',
                              width: 10,
                              left: -4,
                              position: 'absolute',
                            }}
                          />
                        </Box>
                      )}
                    </TableCell>
                  ))}
                  {resizeable && (
                    <TableCell
                      style={{width: 20, minWidth: 20}}
                      sx={{
                        pointerEvents: 'none',
                        border: 'none',
                        background: 'transparent',
                      }}
                    />
                  )}
                </TableRow>
              </TableHead>
            ) : null}
          </Table>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            minHeight: 41,
          }}
        >
          <SimpleBar
            ref={simpleBarRef}
            scrollableNodeProps={{ref: scrollContainerRef}}
            style={{
              maxHeight: '100%',
              minHeight: '100%',
              width: '100%',
              flexGrow: 1,
              ...(data.length === 0 && {
                overflowX: 'hidden',
                overflowY: 'hidden',
              }),
            }}
          >
            <Table
              stickyHeader
              ref={tableRef}
              size={size}
              sx={{
                overflow: 'hidden',
                bgcolor: 'inherit',
                minWidth: '100%',
                height: data.length ? '80%' : undefined,
                ...(resizeable && {tableLayout: 'fixed'}),
              }}
            >
              <TableBody ref={tableBodyRef}>
                {pageData.map((i, rowIdx) => {
                  return draggable ? (
                    <Draggable
                      key={rowIdx}
                      nodeRef={tableRef}
                      axis="y"
                      handle={draggableClass ?? '.draggable-handle'}
                      onDrag={(e, ui) => {
                        handleDrag(e, ui, draggableLayout, rowIdx);
                      }}
                      onStop={(e, ui) =>
                        draggableCallback?.(
                          handleDragStop(e, ui, draggableLayout)
                        )
                      }
                      position={{x: 0, y: 0}}
                    >
                      {renderTableRow(i, rowIdx)}
                    </Draggable>
                  ) : (
                    renderTableRow(i, rowIdx)
                  );
                })}
              </TableBody>
            </Table>
          </SimpleBar>
        </Box>
        {!data.length ? (
          <Box
            display="flex"
            height="100%"
            alignItems="center"
            justifyContent="center"
          >
            {loading
              ? 'Loading...'
              : customNoDataMessage
                ? customNoDataMessage
                : 'No data'}
          </Box>
        ) : null}

        {pagination || footerStart ? (
          <Box
            width="100%"
            height="60px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pl={1}
            sx={sxFooter}
          >
            <Box flexGrow={1}>{footerStart}</Box>

            {pagination ? (
              <TablePagination
                component="div"
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={rowCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_event, v) => handlePageChange(v)}
                onRowsPerPageChange={(event) =>
                  handlePageSizeChange(+event.target.value || 10)
                }
              />
            ) : null}
          </Box>
        ) : null}

        <DataGridArrangeColumnsModal
          open={arrangeModalOpen}
          columns={columns}
          rows={rows}
          shownFields={shownFields}
          defaultShownFields={defaultShownFields}
          columnOrder={columnOrder}
          columnWidths={internalColumnWidths}
          onClose={() => setArrangeModalOpen(false)}
          onApply={(newOrder, newShownFields) => {
            if (onColumnOrderAndShownFieldsChange) {
              onColumnOrderAndShownFieldsChange(newOrder, newShownFields);
            } else {
              handleColumnOrderChange(newOrder);
              handleShowFields(newShownFields);
            }
            setArrangeModalOpen(false);
          }}
        />
      </Box>
    );
  }
) as <T extends DataGridRow>(
  props: Props<T> & {ref?: ForwardedRef<DataGridRef>}
) => JSX.Element;

export default DataGrid;
